import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CreatableSelect from 'react-select/creatable';

import Axios from '../../../networking/intraceptor';
import { pharmacy_delivered_rx_reasons } from '../../../routes/routeName';
import {
	pending_reprocessed_delivery_reviews,
	pending_reprocessed_delivery_reviews_export,
	reprocessed_delivery_review_reasons,
	save_reprocessed_delivery_reviews,
} from '../../../networking/urlEndPoints';
import { APP_TIMEZONE } from '../../../utils/constants/formInitialStates';
import { safeToast } from '../../../utils/helper/helper';
import {
	DELIVERED_RX_REASON_SNOOZE_OPTIONS,
	getDeliveredRxReasonSnoozeUntilDate,
} from './DeliveredRxReasonChangeReviewShared';

/**
 * "Delivered, then reprocessed in WinRx" review.
 *
 * Modelled on DeliveredRxReasonChangeReviewShared, which is in daily use, and
 * deliberately reuses its snooze options and duration maths so the two popups
 * behave identically where they overlap.
 *
 * The product rule is ONE POPUP: the reason and the copy consent are captured
 * together and posted in a single request. There is never a second dialogue.
 */

export const REPROCESSED_DELIVERY_SOCKET_EVENT = 'reprocessed-delivery-review';

export const REPROCESSED_DELIVERY_SETTINGS_PATH = pharmacy_delivered_rx_reasons;

export const REPROCESSED_DELIVERY_SNOOZE_OPTIONS = DELIVERED_RX_REASON_SNOOZE_OPTIONS;

// Must not collide with `deliveredRxReasonChangeReviewSnooze` or
// `quantityChangeReasonReviewSnooze` - snoozing one popup must not silence
// another.
const SNOOZE_STORAGE_PREFIX = 'reprocessedDeliveryReviewSnooze';

const getSnoozeStorageKey = (pharmacyId, userId) =>
	`${SNOOZE_STORAGE_PREFIX}:${pharmacyId || 'pharmacy'}:${userId || 'user'}`;

const getStoredSnoozeUntilMs = (storageKey) => {
	if (!storageKey) return null;

	try {
		const rawValue = localStorage.getItem(storageKey);
		if (!rawValue) return null;

		const parsedValue = JSON.parse(rawValue);
		const snoozeUntilMs = new Date(parsedValue?.snoozeUntil || parsedValue).getTime();

		if (Number.isNaN(snoozeUntilMs) || snoozeUntilMs <= Date.now()) {
			localStorage.removeItem(storageKey);
			return null;
		}

		return snoozeUntilMs;
	} catch (error) {
		localStorage.removeItem(storageKey);
		return null;
	}
};

const collapseWhitespace = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

export const getReprocessedDeliveryRecordKey = (record, index = 0) =>
	String(record?.id || `${record?.rx_number || 'rx'}-${record?.target_dose_id || index}`);

const getRecordIdentityKey = (record) =>
	record?.id
		? `id:${record.id}`
		: [record?.rx_number || 'rx', record?.dose_date || '', record?.case_type || '', record?.target_dose_id || ''].join('|');

/**
 * The copy-related fields the API still returns (`candidate_sources`,
 * `copy_available`, `copy_preselected`, `requires_manual_choice`, ...) are
 * deliberately NOT normalised onto the record any more. The popup shows no copy
 * control, so carrying them here would invite someone to render a proof detail
 * the pharmacist has no way to act on. They remain in the API response for the
 * CSV export and for auditing.
 */
export const normalizeReprocessedDeliveryRecord = (record) => ({
	...record,
	rx_number: record?.rx_number || record?.rxnumber || '',
	case_type: record?.case_type || 'child',
	review_reason: record?.review_reason || '',
});

const mergeRecords = (existing, incoming) => {
	const map = new Map(existing.map((record) => [getRecordIdentityKey(record), record]));
	incoming.forEach((record) => map.set(getRecordIdentityKey(record), record));
	return Array.from(map.values());
};

const removeRecords = (existing, removed) => {
	const removedKeys = new Set(removed.map(getRecordIdentityKey));
	return existing.filter((record) => !removedKeys.has(getRecordIdentityKey(record)));
};

const normalizeReasonOption = (reason) => {
	if (reason && typeof reason === 'object') {
		const description = collapseWhitespace(reason.description || reason.value || reason.label);
		const title = collapseWhitespace(reason.title || reason.label || description);
		const templateId = reason.template_id ?? reason.id ?? null;

		return description ? { value: description, label: title || description, templateId } : null;
	}

	const normalizedReason = collapseWhitespace(reason);
	return normalizedReason ? { value: normalizedReason, label: normalizedReason, templateId: null } : null;
};

const mergeReasonOptions = (reasons = []) => {
	const map = new Map();

	reasons.forEach((reason) => {
		const option = normalizeReasonOption(reason);
		if (!option) return;

		const key = option.templateId ? `template:${option.templateId}` : `text:${option.value.toLowerCase()}`;
		if (!map.has(key)) map.set(key, option);
	});

	return Array.from(map.values());
};

const getSelectedReasonOption = (selection, reasonOptions) => {
	if (!selection) return null;

	const matched = reasonOptions.find(
		(option) =>
			(selection.templateId && option.templateId === selection.templateId) ||
			(!selection.templateId && option.value === selection.value)
	);

	return matched || { value: selection.value, label: selection.value, templateId: selection.templateId ?? null };
};

// Menu portal must sit above the modal overlay (z-[174] below).
const reprocessedReviewSelectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: 42,
		minWidth: 0,
		width: '100%',
		fontSize: 13,
		borderColor: state.selectProps.hasError ? '#fca5a5' : state.isFocused ? '#2563eb' : '#cbd5e1',
		boxShadow: 'none',
		'&:hover': { borderColor: state.selectProps.hasError ? '#fca5a5' : '#94a3b8' },
	}),
	menuPortal: (base) => ({ ...base, zIndex: 222 }),
	menu: (base) => ({ ...base, width: 340, fontSize: 13 }),
	option: (base) => ({ ...base, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
};

const downloadBase64File = (base64Content, fileName, mimeType = 'text/csv') => {
	const binary = atob(base64Content);
	const bytes = new Uint8Array(binary.length);
	for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);

	const blobUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
	const link = document.createElement('a');
	link.href = blobUrl;
	link.download = fileName || 'pending-reprocessed-delivery-reviews.csv';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(blobUrl);
};

/**
 * Seed one row's answer state.
 *
 * The copy is pre-checked only when the backend says the mapping is unambiguous
 * enough to offer it that way (`copy_preselected`). An ambiguous row starts
 * unchecked with no source picked, so a pharmacist has to make a positive choice
 * before any signature moves.
 */
/**
 * Seed one row's answer.
 *
 * The reason is the only thing a pharmacist can set. The copy consent and source
 * selection that used to live here are gone: the backend now copies the delivery
 * proof automatically and picks the source itself.
 */
const buildInitialAnswer = (record) => ({
	reason: record?.review_reason ? { value: record.review_reason, templateId: null } : '',
});

export const useReprocessedDeliveryReviews = ({
	enabled = true,
	pharmacyId,
	userId,
	listenToSocket = true,
	showSuccessToast = true,
	enableSnooze = false,
} = {}) => {
	const [records, setRecords] = useState([]);
	const [answers, setAnswers] = useState({});
	const [fieldErrors, setFieldErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [loading, setLoading] = useState(false);
	const [hasLoaded, setHasLoaded] = useState(false);
	const [savedReasonOptions, setSavedReasonOptions] = useState([]);
	const [snoozeUntilMs, setSnoozeUntilMs] = useState(() =>
		enableSnooze ? getStoredSnoozeUntilMs(getSnoozeStorageKey(pharmacyId, userId)) : null
	);

	const answersRef = useRef(answers);
	answersRef.current = answers;

	const snoozeStorageKey = useMemo(
		() => (enableSnooze ? getSnoozeStorageKey(pharmacyId, userId) : null),
		[enableSnooze, pharmacyId, userId]
	);

	const reasonOptions = useMemo(() => mergeReasonOptions(savedReasonOptions), [savedReasonOptions]);

	// Re-read storage on every call rather than trusting state: a snooze set in
	// another tab, or mid-request, must still suppress the popup.
	const isSnoozed = useCallback(() => {
		if (!enableSnooze || !snoozeStorageKey) return false;

		const storedSnoozeUntilMs = getStoredSnoozeUntilMs(snoozeStorageKey);
		setSnoozeUntilMs(storedSnoozeUntilMs);

		return Boolean(storedSnoozeUntilMs);
	}, [enableSnooze, snoozeStorageKey]);

	const loadPendingRecords = useCallback(async () => {
		if (!enabled || !pharmacyId) return;
		if (isSnoozed()) {
			setRecords([]);
			return;
		}

		setLoading(true);
		try {
			const response = await Axios.get(pending_reprocessed_delivery_reviews);
			const responseData = response?.data?.data || {};
			const nextRecords = Array.isArray(responseData.records)
				? responseData.records.map(normalizeReprocessedDeliveryRecord)
				: [];

			// A snooze set while this request was in flight still wins.
			setRecords(isSnoozed() ? [] : nextRecords);
		} catch (error) {
			safeToast.error('Unable to load reprocessed delivery reviews.');
		} finally {
			setLoading(false);
			setHasLoaded(true);
		}
	}, [enabled, pharmacyId, isSnoozed]);

	const loadSavedReasonOptions = useCallback(async () => {
		if (!enabled || !pharmacyId) return;

		try {
			const response = await Axios.get(reprocessed_delivery_review_reasons);
			const responseData = response?.data?.data || {};
			const nextOptions = Array.isArray(responseData.templates)
				? responseData.templates
				: Array.isArray(responseData.options)
					? responseData.options
					: Array.isArray(responseData.reasons)
						? responseData.reasons
						: [];

			setSavedReasonOptions(nextOptions);
		} catch (error) {
			safeToast.error('Unable to load reprocessed delivery review reasons.');
		}
	}, [enabled, pharmacyId]);

	useEffect(() => {
		loadPendingRecords();
		loadSavedReasonOptions();
	}, [loadPendingRecords, loadSavedReasonOptions]);

	// When the snooze expires the popup reopens itself, no page refresh needed.
	useEffect(() => {
		if (!enableSnooze || !snoozeUntilMs) return undefined;

		const delay = snoozeUntilMs - Date.now();
		if (delay <= 0) {
			if (snoozeStorageKey) localStorage.removeItem(snoozeStorageKey);
			setSnoozeUntilMs(null);
			loadPendingRecords();
			return undefined;
		}

		const timerId = setTimeout(() => {
			if (snoozeStorageKey) localStorage.removeItem(snoozeStorageKey);
			setSnoozeUntilMs(null);
			loadPendingRecords();
		}, delay);

		return () => clearTimeout(timerId);
	}, [enableSnooze, snoozeUntilMs, snoozeStorageKey, loadPendingRecords]);

	// Keep the answer map in step with the record list, preserving anything the
	// pharmacist has already typed or ticked.
	useEffect(() => {
		setAnswers((previous) => {
			const next = {};

			records.forEach((record, index) => {
				const key = getReprocessedDeliveryRecordKey(record, index);
				next[key] = previous[key] ?? buildInitialAnswer(record);
			});

			return next;
		});
		setFieldErrors({});
	}, [records]);

	useEffect(() => {
		if (!enabled || !pharmacyId || !userId || !listenToSocket) {
			setRecords([]);
			return undefined;
		}

		const handleRealtimeEvent = (event) => {
			const detail = event?.detail || {};
			if (detail.event !== REPROCESSED_DELIVERY_SOCKET_EVENT) return;

			const payload = Array.isArray(detail.payload) ? detail.payload[0] : detail.payload || {};
			if (payload?.pharmacy_id && Number(payload.pharmacy_id) !== Number(pharmacyId)) return;
			if (isSnoozed()) return;

			const incoming = Array.isArray(payload?.records)
				? payload.records.map(normalizeReprocessedDeliveryRecord)
				: [];

			if (!incoming.length) {
				loadPendingRecords();
				return;
			}

			setRecords((existing) =>
				payload?.action === 'acknowledged' ? removeRecords(existing, incoming) : mergeRecords(existing, incoming)
			);
		};

		window.addEventListener('oatrx:realtime:event', handleRealtimeEvent);
		return () => window.removeEventListener('oatrx:realtime:event', handleRealtimeEvent);
	}, [enabled, pharmacyId, userId, listenToSocket, isSnoozed, loadPendingRecords]);

	const handleReasonChange = useCallback((recordKey, option) => {
		setAnswers((previous) => ({
			...previous,
			[recordKey]: {
				...(previous[recordKey] || {}),
				reason: option ? { value: option.value, templateId: option.templateId ?? null } : null,
			},
		}));
		setFieldErrors((previous) => ({ ...previous, [recordKey]: null }));
	}, []);

	const handleSkip = useCallback(
		({ minutes, customTime } = {}) => {
			if (!enableSnooze || !snoozeStorageKey) {
				setRecords([]);
				setAnswers({});
				setFieldErrors({});
				return;
			}

			const snoozeUntilDate = getDeliveredRxReasonSnoozeUntilDate({ minutes, customTime });
			if (!snoozeUntilDate || snoozeUntilDate.getTime() <= Date.now()) return;

			localStorage.setItem(
				snoozeStorageKey,
				JSON.stringify({ snoozeUntil: snoozeUntilDate.toISOString(), timeZone: APP_TIMEZONE })
			);
			setSnoozeUntilMs(snoozeUntilDate.getTime());
			setRecords([]);
			setAnswers({});
			setFieldErrors({});
		},
		[enableSnooze, snoozeStorageKey]
	);

	const handleExportCsv = useCallback(async () => {
		setExporting(true);
		try {
			const response = await Axios.get(pending_reprocessed_delivery_reviews_export);
			const responseData = response?.data?.data || {};
			if (!responseData.content) throw new Error('empty export');

			downloadBase64File(responseData.content, responseData.filename, responseData.mime_type);
		} catch (error) {
			safeToast.error('Unable to export reprocessed delivery reviews.');
		} finally {
			setExporting(false);
		}
	}, []);

	const handleSubmit = useCallback(async () => {
		const current = answersRef.current;

		// The reason is the whole payload. `copy_proof` and `copy_source_dose_id`
		// are deliberately not sent - the endpoint no longer accepts them, and
		// the backend chooses the source itself.
		const recordsToSave = records
			.map((record, index) => {
				const key = getReprocessedDeliveryRecordKey(record, index);
				const answer = current[key] || {};
				const reasonValue = collapseWhitespace(answer.reason?.value);

				if (!reasonValue) return null;

				return {
					key,
					record,
					payload: {
						id: record.id,
						review_reason: reasonValue,
						reason_template_id: answer.reason?.templateId ?? null,
					},
				};
			})
			.filter(Boolean);

		if (!recordsToSave.length) {
			safeToast.error('Select at least one reason.');
			return;
		}

		setSubmitting(true);
		try {
			const response = await Axios.post(save_reprocessed_delivery_reviews, {
				records: recordsToSave.map(({ payload }) => payload),
			});

			// Only drop rows the backend actually acknowledged. A declined parent
			// row stays pending on purpose so the popup returns for it.
			const acknowledgedIds = new Set(
				(response?.data?.data?.records || [])
					.filter((saved) => saved?.acknowledged !== false)
					.map((saved) => saved.id)
			);
			setRecords((existing) => existing.filter((record) => !acknowledgedIds.has(record.id)));

			if (showSuccessToast) safeToast.success('Reprocessed delivery review saved.');

			loadSavedReasonOptions();
			loadPendingRecords();
		} catch (error) {
			const validationErrors = error?.response?.data?.data?.error;
			if (validationErrors) {
				safeToast.error(Object.values(validationErrors).flat()[0] || 'Unable to save reprocessed delivery review.');
			} else {
				safeToast.error('Unable to save reprocessed delivery review.');
			}
		} finally {
			setSubmitting(false);
		}
	}, [records, showSuccessToast, loadPendingRecords, loadSavedReasonOptions]);

	return {
		records,
		answers,
		reasonOptions,
		fieldErrors,
		submitting,
		exporting,
		loading,
		hasLoaded,
		loadPendingRecords,
		handleReasonChange,
		handleExportCsv,
		handleSubmit,
		handleSkip,
		setRecords,
	};
};

/**
 * One row per replacement dose (or one per prescription for the OD/parent case).
 *
 * THE REASON IS THE ONLY INPUT. There is no copy checkbox, no source picker and
 * no proof detail: on submit the backend copies the delivery proof
 * automatically, choosing the source itself. Anything shown here about which
 * delivery would be reused would be decoration the pharmacist cannot act on.
 */
const ReprocessedDeliveryReviewTable = ({
	records,
	answers,
	reasonOptions = [],
	errors,
	submitting,
	onReasonChange,
}) => (
	<table className="w-full min-w-[1040px] table-fixed text-left text-sm">
		<thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-100 text-xs font-semibold uppercase text-slate-600">
			<tr>
				<th className="w-[4%] px-4 py-3">#</th>
				<th className="w-[16%] px-3 py-3">Patient</th>
				<th className="w-[9%] px-3 py-3">RX</th>
				<th className="w-[18%] px-3 py-3">Drug</th>
				<th className="w-[9%] px-3 py-3">Dose</th>
				<th className="w-[9%] px-3 py-3">Reversal</th>
				<th className="w-[11%] px-3 py-3">Dose Date</th>
				<th className="w-[24%] px-4 py-3">Reason</th>
			</tr>
		</thead>
		<tbody className="divide-y divide-slate-200">
			{records.map((record, index) => {
				const recordKey = getReprocessedDeliveryRecordKey(record, index);
				const answer = answers[recordKey] || {};
				const error = errors?.[recordKey];

				return (
					<tr key={recordKey} className="align-top transition-colors hover:bg-slate-50/70">
						<td className="px-4 py-4 text-slate-500">{index + 1}</td>
						<td className="break-words px-3 py-4 font-semibold text-slate-900">{record.patient_name}</td>
						<td className="px-3 py-4 font-medium text-slate-700">{record.rx_number}</td>
						<td className="break-words px-3 py-4 text-slate-700">{record.drug}</td>
						<td className="px-3 py-4 text-slate-700">
							{/* Every row is tied to a reprocessed dose now, once-daily
							    included, so the dose number always applies. */}
							{[record.frequency, record.target_dose_no ? `#${record.target_dose_no}` : null]
								.filter(Boolean)
								.join(' ')}
						</td>
						<td className="px-3 py-4">
							<span className="inline-flex rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-700">
								{record.reversal_reason}
							</span>
						</td>
						<td className="px-3 py-4 text-slate-700">{record.dose_date_display || record.dose_date}</td>
						<td className="px-4 py-3">
							{reasonOptions.length === 0 ? (
								<div className="rounded-md border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-600">
									No reasons set up yet. Add them under{' '}
									<a className="font-semibold text-blue-600" href={REPROCESSED_DELIVERY_SETTINGS_PATH}>
										Reprocess Reasons
									</a>
									, then reopen this popup.
								</div>
							) : (
								<CreatableSelect
									inputId={`reprocessed-delivery-review-${recordKey}`}
									options={reasonOptions}
									value={getSelectedReasonOption(answer.reason, reasonOptions)}
									onChange={(option) => onReasonChange(recordKey, option)}
									onCreateOption={(value) =>
										onReasonChange(recordKey, { value: collapseWhitespace(value), label: collapseWhitespace(value), templateId: null })
									}
									isClearable
									isSearchable
									isDisabled={submitting}
									hasError={Boolean(error)}
									classNamePrefix="react-select"
									styles={reprocessedReviewSelectStyles}
									menuPortalTarget={document.body}
									menuPosition="fixed"
									placeholder="Select reason"
									formatCreateLabel={(value) => `Use custom reason: ${value}`}
									noOptionsMessage={() => 'No matching reason'}
								/>
							)}
						</td>
					</tr>
				);
			})}
		</tbody>
	</table>
);

export default ReprocessedDeliveryReviewTable;
